import overlay_img from 'images/loginb2.webp'
import {useFormik} from 'formik'
import React, {useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useSearchParams} from 'react-router-dom'
import {AuthSignHeader} from 'i18n/AuthSignHeader'
import {LocalLink} from 'i18n/LocalLink'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import {getLoginSchema} from 'utils/validation-schema'
import {useI18n} from 'i18n/I18nContext'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {routes} from 'routes/routes'
import {LoginData} from 'organization/Auth/Interfaces/login'
import GoogleIcon from 'assets/icons/svg/GoogleIcon'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'
import {withLocalePath} from 'i18n/localePaths'
import {OrgPromoOfferBanner} from 'organization/Auth/OrgPromoOfferBanner'
import {normalizePromoInviteToken} from 'organization/promoInviteToken'

const ErrorMsg = ({errors}: any) => {
  return (
    <>
      <p className="error-msg" style={{color: 'red'}}>
        {errors}
      </p>
    </>
  )
}

export function Login() {
  const [isBtnDisable, setBtnDisable] = useState(false)
  const [searchParams] = useSearchParams()
  const inviteFromUrl = normalizePromoInviteToken(searchParams.get('invite'))

  const navigate = useLocalizedNavigate()
  const {login, googleAuth} = useOrganizationAuth()
  const {t, locale} = useI18n()
  const orgLoginPath = withLocalePath(locale, routes.organization.login)
  const orgLoginUrl = `${SITE_ORIGIN}${orgLoginPath}`
  const loginSchema = useMemo(() => getLoginSchema(t), [t])

  const {handleSubmit, handleBlur, handleChange, values, errors, touched} =
    useFormik({
      initialValues: {
        email: '',
        password: '',
        remember: false,
      },
      validationSchema: loginSchema,
      onSubmit: async (values: LoginData) => {
        setBtnDisable(true)
        const loginResult = await login({
          ...values,
          promo_invite_token: inviteFromUrl ?? undefined,
        })

        if (loginResult === true) {
          toast.success(t('auth.loginSuccess'))
          navigate(routes.organization.list)
        } else if (
          typeof loginResult === 'object' &&
          loginResult?.requiresVerification
        ) {
          navigate(
            `${routes.organization.verifyEmail}?email=${encodeURIComponent(
              loginResult.email,
            )}`,
          )
        }

        setBtnDisable(false)
      },
    })

  const googleLogin = () => {
    void googleAuth(inviteFromUrl)
  }

  return (
    <>
      <Helmet>
        <title>{t('auth.orgLoginTitle')}</title>
        <meta name="robots" content="noindex,follow" />
        <meta name="description" content={t('auth.orgLoginMeta')} />
        <link rel="canonical" href={orgLoginUrl} />
        <meta property="og:title" content={t('auth.orgLoginOgTitle')} />
        <meta property="og:url" content={orgLoginUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <main className={`body-area bd-theme-light`}>
        <section className="signin__area">
          <div className="sign__main-wrapper">
            <div className="sign__left">
              <AuthSignHeader
                logo={
                  <div className="sign__logo">
                    <LocalLink to={routes.index}>
                      <img
                        style={{width: '150px', height: 'auto'}}
                        className="logo-black"
                        src={process.env.PUBLIC_URL + '/logo-dark.svg'}
                        alt={t('logo.alt')}
                      />
                    </LocalLink>
                  </div>
                }
                navLinks={
                  <div className="sign__link custome_link">
                    <LocalLink
                      className="sign__link-active"
                      to={routes.organization.login}
                    >
                      {t('common.login')}
                    </LocalLink>
                    <LocalLink
                      className="sign__link-text"
                      to={routes.organization.register}
                    >
                      {t('common.register')}
                    </LocalLink>
                  </div>
                }
              />
              <div className="sign__center-wrapper text-center mt-35">
                <div className="sign__title-wrapper mb-40">
                  <h3 className="sign__title">
                    {t('auth.attendeeWelcomeTitle')}
                  </h3>
                  <p>{t('auth.orgSignInSubtitle')}</p>
                  {inviteFromUrl ? (
                    <OrgPromoOfferBanner
                      inviteToken={inviteFromUrl}
                      variant="login"
                    />
                  ) : null}
                </div>
                <div className="sign__input-form text-center">
                  <form onSubmit={handleSubmit}>
                    <div className="sign__input mb-32">
                      <input
                        type="text"
                        placeholder={t('auth.orgEmailLongPlaceholder')}
                        name="email"
                        defaultValue={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      />
                      <span>
                        {' '}
                        <i className="flaticon-user-2"></i>
                      </span>{' '}
                      <br />
                      {touched.email && <ErrorMsg errors={errors.email} />}
                    </div>

                    <div className="sign__input mb-32">
                      <input
                        type="password"
                        placeholder={t('auth.passwordPlaceholder')}
                        name="password"
                        defaultValue={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      />
                      <span>
                        <i className="flaticon-password"></i>
                      </span>
                      {touched.password && (
                        <ErrorMsg errors={errors.password} />
                      )}
                    </div>
                    <div className="sign__action">
                      <div className="sign__check">
                        <input
                          className="e-check-input"
                          type="checkbox"
                          name="remember"
                          id="register-user"
                        />
                        <label
                          className="sign__check-text"
                          htmlFor="register-user"
                        >
                          {t('auth.rememberMe')}
                        </label>
                      </div>
                      <div className="sign__forget">
                        <span>
                          <LocalLink to={routes.organization.passwordRecover}>
                            {t('auth.forgotPassword')}
                          </LocalLink>
                        </span>
                      </div>
                    </div>
                    <div className="sing__button mb-20">
                      <button
                        disabled={isBtnDisable}
                        className={
                          isBtnDisable
                            ? 'disableBtnStyle w-100 mb-20'
                            : 'input__btn w-100 mb-20'
                        }
                        type="submit"
                      >
                        {t('common.login')}
                      </button>
                    </div>
                  </form>
                  <div
                    style={{
                      marginTop: 8,
                      marginBottom: 8,
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    <p
                      style={{
                        margin: '14px 0 10px',
                        fontSize: 14,
                        color: 'var(--clr-text-10, #7a7a7a)',
                        fontWeight: 500,
                      }}
                    >
                      {t('auth.orDividerShort')}
                    </p>
                    <button
                      className="gamil__sign-btn w-100"
                      onClick={googleLogin}
                    >
                      <span>
                        <GoogleIcon />
                      </span>{' '}
                      {t('auth.signInWithGoogle')}
                    </button>
                  </div>

                  <div className="if__account mt-85">
                    <p>
                      {t('auth.orgNoAccountYet')}
                      <LocalLink to={routes.organization.register}>
                        <br />
                        {t('common.register')}
                      </LocalLink>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="sign__right">
              <div
                className="sign__input-thumb"
                style={{
                  backgroundImage: `url(${overlay_img})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                }}
              ></div>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
